import { IconInfo } from '@consta/icons/IconInfo'
import { Button } from '@consta/uikit/Button'
import type { TextPropView } from '@consta/uikit/Text'
import { withTooltip } from '@consta/uikit/withTooltip'
import { IconExpandHalfDown, IconExpandHalfUp } from '@library/components'
import type { Nullable } from '@library/types'
import cn from 'classnames'
import type { ComponentProps, FC, PropsWithChildren, ReactNode } from 'react'
import { useRef, useState } from 'react'

import { SettingLabel } from './SettingLabel'
import css from './SettingsBlock.css'

type SettingContainerProps = ComponentProps<'div'> & {
  label?: string
  className?: string
  delimiterClassName?: string
  isInitiallyOpen?: boolean
  info?: ReactNode
  view?: TextPropView
}

const IconInfoWithTooltip = withTooltip({
  mode: 'mouseover',
  direction: 'downCenter',
  appearTimeout: 300,
  size: 'l',
})(IconInfo)

export const SettingBlock: FC<PropsWithChildren<SettingContainerProps>> = ({
  label,
  children,
  className,
  delimiterClassName,
  isInitiallyOpen = false,
  info,
  view,
  ...rest
}) => {
  const [isShow, setIsShow] = useState<boolean>(isInitiallyOpen)
  const ref = useRef<Nullable<HTMLDivElement>>(null)

  const ButtonIcon = isShow ? IconExpandHalfUp : IconExpandHalfDown

  const toggleIsShow = () => {
    setIsShow(prev => !prev)
  }

  return (
    <div ref={ref} className={cn(css.block, className)} {...rest}>
      <div className={css.blockHeader}>
        <div className={css.blockHeaderLeft}>
          <SettingLabel text={label} view={view} />
          {!!info && (
            <IconInfoWithTooltip size="xs" view="ghost" tooltipProps={{ tooltipContent: info }} />
          )}
        </div>
        <Button
          view="clear"
          size="xs"
          iconLeft={ButtonIcon}
          onlyIcon={true}
          onClick={toggleIsShow}
        />
      </div>
      <div className={cn(css.divider, delimiterClassName)} role="separator" />
      {isShow ? children : null}
    </div>
  )
}
